import {
  BodyText,
  Button,
  Input,
  LinkText,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@postscript/components';
import FormikField from 'components/billing/common/FormikField';
import { useGlobalModal } from 'components/GlobalModal/globalModal';
import { useUser } from 'controllers/contexts/user';
import { Form, Formik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import { useUpdateContactInfo } from '../../account/AccountView/hooks/useUpdateContactInfo';
import { useResubmitTFNVerification } from '../hooks/useTFNVerification';

const NameSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
  margin-top: var(--spacing-3);
`;

const TextSection = styled.div`
  margin-bottom: var(--spacing-3);
`;

const BulletList = styled.ul`
  padding-left: var(--spacing-4);
`;

const NameRow = styled.div`
  display: flex;
  gap: var(--spacing-3);

  & > * {
    flex: 1;
  }
`;

const HelperText = styled(BodyText)`
  margin-top: var(--spacing-1);
`;

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
}

const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Business email is required'),
});

export default function InvalidContactModal(): JSX.Element {
  const { hideModal } = useGlobalModal();
  const {
    user: {
      postscript_contact_email: currentEmail,
      data: { first_name: firstName, last_name: lastName },
      postscript_contact_phone: phone,
    },
  } = useUser();
  const { mutateAsync: updateContactInfo } = useUpdateContactInfo();
  const { mutateAsync: resubmitTFNVerification } = useResubmitTFNVerification();

  const initialValues: FormValues = {
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    email: currentEmail ?? '',
  };

  const handleSubmit = async (values: FormValues) => {
    await updateContactInfo({
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: phone ?? '',
    });

    await resubmitTFNVerification();
    hideModal();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur
      enableReinitialize
    >
      {({ isSubmitting }) => (
        <Form>
          <ModalHeader onCancel={hideModal}>
            Toll-Free Number verification denied
          </ModalHeader>
          <ModalBody>
            <TextSection>
              <BodyText bold size="small">
                Denial reason
              </BodyText>
              <BodyText size="small">
                Contact must be a business representative matching official
                records (owner or authorized employee).
              </BodyText>
            </TextSection>

            <TextSection>
              <BodyText bold size="small">
                Ensure that your...
              </BodyText>
              <BulletList>
                <li>
                  <BodyText size="small">
                    Contact name is a real representative
                  </BodyText>
                </li>
                <li>
                  <BodyText size="small">
                    Contact info is correct and reachable
                  </BodyText>
                </li>
                <li>
                  <BodyText size="small">
                    Business name matches registration (avoid informal
                    variants/abbreviations unless officially recognized)
                  </BodyText>
                </li>
              </BulletList>
            </TextSection>
            <TextSection>
              <BodyText size="small">
                <strong>
                  Please update your contact details to address the above
                  criteria, then resubmit
                </strong>
                . Verification typically takes up to 5 business days. Your
                website must remain live during this time.
              </BodyText>
            </TextSection>
            <NameSection>
              <NameRow>
                <FormikField
                  name="firstName"
                  label="First name"
                  placeholder="First name"
                  type="text"
                  as={Input}
                  required
                  fieldSize="medium"
                />
                <FormikField
                  name="lastName"
                  label="Last name"
                  placeholder="Last name"
                  type="text"
                  as={Input}
                  required
                  fieldSize="medium"
                />
              </NameRow>
              <div>
                <FormikField
                  name="email"
                  label="Business email"
                  placeholder="name@yourstore.com"
                  type="email"
                  as={Input}
                  required
                  fieldSize="medium"
                />
                <HelperText size="x-small" color="var(--gray-5)">
                  You can also find these fields under{' '}
                  <LinkText
                    href="/account/settings/general#contact-info"
                    onClick={hideModal}
                  >
                    Shop settings → General → Business contact information
                  </LinkText>
                </HelperText>
              </div>
            </NameSection>
          </ModalBody>
          <ModalFooter>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              Save and Resubmit
            </Button>
          </ModalFooter>
        </Form>
      )}
    </Formik>
  );
}
